import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { flattenSimple } from '@fi-shared/utils/flatten-simple';
import { FiChecklist2ReportDetailsViewerBase } from '../../../shared/fi-checklist2-report-details-viewer-base';
import { IChecklist2ReportAnswer, IChecklist2ReportAttentionRequired } from '../../../shared/fi-checklist2-report';
import {
    FiChecklist2ReportType,
    WidgetTableFilterType,
} from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { union } from '@fi-shared/utils/fi-union';
import { FiChecklistReportAvailableBusinessUnitsChecker } from '@fi-shared/checklist/fi-checklist2-report/components/shared/available-business-uints-checker';

@Component({
    selector: 'fi-deviation-checklist-details',
    templateUrl: './deviation-checklist-details.component.html',
    styleUrls: ['./deviation-checklist-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-deviation-checklist-details' },
    standalone: false,
})
export class FiDeviationChecklistDetailsComponent extends FiChecklist2ReportDetailsViewerBase implements OnInit {
    @Input() checkpointDetailsId: number;
    answerDetails$ = new BehaviorSubject<IChecklist2ReportAnswer[]>([]);
    currentCheckpoint: IChecklist2ReportAnswer;
    protected availableBusinessUnitsChecker: FiChecklistReportAvailableBusinessUnitsChecker;
    private _availableScheduleIds$ = new BehaviorSubject<number[]>(undefined);
    protected get availableScheduleIds$(): Observable<number[]> {
        return this._availableScheduleIds$.asObservable();
    }

    ngOnInit() {
        super.ngOnInit();
        this.setAvailableScheduleIds();

        // for smart updating available business units on filter change
        this.availableBusinessUnitsChecker = new FiChecklistReportAvailableBusinessUnitsChecker(
            this.injector,
            FiChecklist2ReportType.AttentionRequired,
            this.reportData.auditMode,
        );

        this.loadDeviationDetails();
        this.fiChecklist2ReportDataService.subscribeOnFilterOrDateRangeChanged(
            this.reportParameter.Id,
            this.ngOnDestroy$,
            () => {
                this.loadDeviationDetails();
            },
        );
    }

    protected setInitialFilterData() {
        super.setInitialFilterData();
        this.currentFilter.CheckpointIds = [this.checkpointDetailsId];
    }

    private loadDeviationDetails(): Promise<void> {
        const promise = new Promise<void>((resolve, reject) => {
            this.fiChecklist2ReportOpenChecklistsService
                .getDeviationDetails(this.currentFilter, this.reportData.auditMode)
                .then((deviationDetails) => {
                    this.answerDetails$.next(deviationDetails);

                    this.availableBusinessUnitsChecker.setAvailableBusinessUnitIds(
                        deviationDetails.map((unit) => unit.BusinessUnitId),
                        this.currentFilter,
                    );

                    resolve();
                }, reject);
        });
        promise.then(() => this.setCurrentCheckpointUnitData());
        return promise;
    }

    private setCurrentCheckpointUnitData() {
        this.currentCheckpoint = this.answerDetails$.value.find((deviation: IChecklist2ReportAnswer) => {
            return deviation.CheckpointId === this.checkpointDetailsId;
        });
    }

    private setAvailableScheduleIds() {
        const availableScheduleIds: number[] = union(
            this.deviationsSummary
                .filter((item) => {
                    if (this.currentFilter.CheckpointIds?.length) {
                        return this.currentFilter.CheckpointIds.includes(item.CheckpointId);
                    }
                    return true;
                })
                .map((item) => item.ScheduleId),
        );
        this._availableScheduleIds$.next(availableScheduleIds);
    }

    getNewDeviationAnswerIds(): number[] {
        return flattenSimple(
            this.deviationsSummary.map((attentionRequired: IChecklist2ReportAttentionRequired) => {
                return attentionRequired.NewAnswers.map((item) => item.AnswerId);
            }),
        );
    }

    protected readonly WidgetTableFilterType = WidgetTableFilterType;
}
